import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { referralsAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { Wallet, Mail, Lock, User, Phone, ArrowRight, AlertCircle, Check, ArrowLeft, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CountrySelector from '@/components/CountrySelector';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code) {
      setReferrerName('');
      return;
    }
    
    setValidatingReferral(true);
    try {
      const response = await referralsAPI.validateCode(code);
      if (response.data.valid) {
        setReferrerName(response.data.referrer_name);
      }
    } catch (err) {
      setReferrerName('');
      if (code.length >= 6) {
        toast.error('Invalid referral code');
      }
    } finally {
      setValidatingReferral(false);
    }
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', met: formData.password.length >= 8 },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(formData.password) },
    { label: 'Contains number', met: /[0-9]/.test(formData.password) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate country selection
    if (!formData.country) {
      setError('Please select your country');
      toast.error('Please select your country');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const allMet = passwordRequirements.every(r => r.met);
    if (!allMet) {
      setError('Please meet all password requirements');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        password: formData.password,
        referral_code: referralCode || undefined
      });
      toast.success('Registration successful! Please check your email for verification code.');
      navigate('/verify-otp', { state: { email: formData.email } });
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 order-2 lg:order-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-5 sm:space-y-6"
        >
          {/* Back Button - Mobile */}
          <button
            onClick={() => navigate('/')}
            className="lg:hidden flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-2"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="register-logo-link">
            <Wallet className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-400" />
            <span className="text-xl sm:text-2xl font-outfit font-bold gradient-text">Dexfolio</span>
          </Link>

          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Create Account</h1>
            <p className="text-white/60 mt-1 sm:mt-2 text-sm sm:text-base">Start your trading journey today</p>
          </div>

          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 rounded-lg bg-hotpink-400/10 border border-hotpink-400/30 text-hotpink-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/80 text-sm">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  className="input-field pl-10"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="input-field pl-10"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/80 text-sm">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1234567890"
                    className="input-field pl-10"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" className="text-white/80 text-sm">
                  Country <span className="text-hotpink-400">*</span>
                </Label>
                <CountrySelector
                  value={formData.country}
                  onChange={(country) => setFormData({ ...formData, country })}
                  error={error && !formData.country}
                />
                {!formData.country && (
                  <p className="text-white/40 text-xs">Required field</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a strong password"
                  className="input-field pl-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="register-password-input"
                />
              </div>
              {/* Password Requirements */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    {req.met ? (
                      <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-white/30 shrink-0" />
                    )}
                    <span className={req.met ? 'text-emerald-400' : 'text-white/50'}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80 text-sm">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  className="input-field pl-10"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            {/* Referral Code */}
            <div className="space-y-2">
              <Label htmlFor="referral" className="text-white/80 text-sm flex items-center gap-2">
                <Gift className="w-4 h-4 text-cyan-400" />
                Referral Code (Optional)
              </Label>
              <div className="relative">
                <Input
                  id="referral"
                  type="text"
                  placeholder="Enter referral code"
                  className="input-field uppercase tracking-wider"
                  value={referralCode}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase();
                    setReferralCode(code);
                    if (code.length >= 8) {
                      validateReferralCode(code);
                    } else {
                      setReferrerName('');
                    }
                  }}
                  data-testid="register-referral-input"
                />
                {validatingReferral && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {referrerName && !validatingReferral && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-400" />
                )}
              </div>
              {referrerName && (
                <p className="text-xs text-emerald-400 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Referred by {referrerName}
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-white/60 text-sm sm:text-base">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden order-1 lg:order-2">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1642062171116-6b5d4eb924e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxjcnlwdG8lMjBjb2lucyUyMGdsYXNzfGVufDB8fHx8MTc3MzQ3OTQzNXww&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#020408]" />
        
        <div className="relative z-10 text-center max-w-md px-8">
          <h2 className="text-3xl lg:text-4xl font-outfit font-bold text-white mb-4">
            Join the <span className="gradient-text">Revolution</span>
          </h2>
          <p className="text-white/60">
            Start earning daily returns with our automated cryptocurrency trading platform.
          </p>
        </div>
      </div>

      {/* Mobile Hero Section */}
      <div className="lg:hidden relative h-36 sm:h-48 overflow-hidden order-1">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1642062171116-6b5d4eb924e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxjcnlwdG8lMjBjb2lucyUyMGdsYXNzfGVufDB8fHx8MTc3MzQ3OTQzNXww&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020408] via-transparent to-[#020408]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <h2 className="text-xl sm:text-2xl font-outfit font-bold text-white mb-2">
              Join the <span className="gradient-text">Revolution</span>
            </h2>
            <p className="text-white/60 text-sm">Start earning daily returns today</p>
          </div>
        </div>
      </div>
    </div>
  );
}
